import { useState, useEffect } from 'react'
import { AuthChangeRedirector, AnonymousRoute, AuthenticatedRoute } from './auth'
import {
  createBrowserRouter,
  Navigate,
  RouterProvider
} from 'react-router-dom'
import { Spin } from 'antd'
import Login from './account/Login'
import RequestLoginCode from './account/RequestLoginCode'
import ConfirmLoginCode from './account/ConfirmLoginCode'
import Logout from './account/Logout'
import Signup from './account/Signup'
import ProviderSignup from './socialaccount/ProviderSignup'
import ProviderCallback from './socialaccount/ProviderCallback'
import Home from './Home'
import ChangeEmail from './account/ChangeEmail'
import ManageProviders from './socialaccount/ManageProviders'
import VerifyEmail, { loader as verifyEmailLoader } from './account/VerifyEmail'
import VerifyEmailByCode from './account/VerifyEmailByCode'
import VerificationEmailSent from './account/VerificationEmailSent'
import RequestPasswordReset from './account/RequestPasswordReset'
import ConfirmPasswordResetCode from './account/ConfirmPasswordResetCode'
import ChangePassword from './account/ChangePassword'
import MFAOverview, { loader as mfaOverviewLoader } from './mfa/MFAOverview'
import ActivateTOTP, { loader as activateTOTPLoader } from './mfa/ActivateTOTP'
import DeactivateTOTP from './mfa/DeactivateTOTP'
import RecoveryCodes, { loader as recoveryCodesLoader } from './mfa/RecoveryCodes'
import AddWebAuthn from './mfa/AddWebAuthn'
import SignupByPasskey from './mfa/SignupByPasskey'
import ReauthenticateWebAuthn from './mfa/ReauthenticateWebAuthn'
import ListWebAuthn, { loader as listWebAuthnLoader } from './mfa/ListWebAuthn'
import GenerateRecoveryCodes, { loader as generateRecoveryCodesLoader } from './mfa/GenerateRecoveryCodes'
import { resetPasswordByLinkLoader, ResetPasswordByCode, ResetPasswordByLink } from './account/ResetPassword'
import AuthenticateTOTP from './mfa/AuthenticateTOTP'
import AuthenticateRecoveryCodes from './mfa/AuthenticateRecoveryCodes'
import AuthenticateWebAuthn from './mfa/AuthenticateWebAuthn'
import ReauthenticateRecoveryCodes from './mfa/ReauthenticateRecoveryCodes'
import ReauthenticateTOTP from './mfa/ReauthenticateTOTP'
import CreateSignupPasskey from './mfa/CreateSignupPasskey'
import Trust from './mfa/Trust'
import Reauthenticate from './account/Reauthenticate'
import Sessions from './usersessions/Sessions'
import Root from './Root'
import { useConfig } from './auth/hooks'
import Dashboard from './appraisals/Dashboard'
import EmployeeForm from './appraisals/EmployeeForm'
import ManagerForm from './appraisals/ManagerForm'
import HeadSignoff from './appraisals/HeadSignoff'
import BuHeadSignoff from './appraisals/BuHeadSignoff'
import ApproverSignoffPage from './appraisals/ApproverSignoffPage'
import AppraisalDetail from './appraisals/AppraisalDetail'
import SessionDetail from './appraisals/SessionDetail'
import BusinessUnitEditPage from './appraisals/BusinessUnitEditPage'
import DepartmentEditPage from './appraisals/DepartmentEditPage'
import NotificationDashboard from './appraisals/NotificationDashboard'
import TranslationReviewPage from './appraisals/TranslationReviewPage'
import NotFound from './NotFound'
import * as api from './lib/api'

function DjangoStaffRoute({ children }) {
  const [me, setMe] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    api.getMe()
      .then(setMe)
      .catch(() => setMe(null))
      .finally(() => setLoading(false))
  }, [])

  if (loading) return <div style={{ textAlign: 'center', padding: 60 }}><Spin /></div>
  if (!me?.is_staff && !me?.is_superuser) return <Navigate to='/dashboard' replace />
  return children
}

function createRouter(config) {
  return createBrowserRouter([
    {
      path: '/',
      element: <AuthChangeRedirector><Root /></AuthChangeRedirector>,
      children: [
        {
          path: '/',
          element: <Home />
        },
        {
          path: '/dashboard',
          element: <AuthenticatedRoute><Dashboard /></AuthenticatedRoute>
        },
        {
          path: '/notifications',
          element: <AuthenticatedRoute><NotificationDashboard /></AuthenticatedRoute>
        },
        {
          path: '/appraisal-templates/new',
          element: <AuthenticatedRoute><DjangoStaffRoute><BusinessUnitEditPage /></DjangoStaffRoute></AuthenticatedRoute>
        },
        {
          path: '/appraisal-templates/:id/edit',
          element: <AuthenticatedRoute><DjangoStaffRoute><BusinessUnitEditPage /></DjangoStaffRoute></AuthenticatedRoute>
        },
        {
          path: '/departments/new',
          element: <AuthenticatedRoute><DepartmentEditPage /></AuthenticatedRoute>
        },
        {
          path: '/departments/:id/edit',
          element: <AuthenticatedRoute><DepartmentEditPage /></AuthenticatedRoute>
        },
        {
          path: '/appraisals/:id',
          element: <AuthenticatedRoute><AppraisalDetail /></AuthenticatedRoute>
        },
        {
          path: '/translation-review',
          element: <AuthenticatedRoute><TranslationReviewPage /></AuthenticatedRoute>
        },
        {
          path: '/appraisals/:id/employee',
          element: <AuthenticatedRoute><EmployeeForm /></AuthenticatedRoute>
        },
        {
          path: '/appraisals/:id/manager',
          element: <AuthenticatedRoute><ManagerForm /></AuthenticatedRoute>
        },
        {
          path: '/appraisals/:id/manager/feedback/:reviewId',
          element: <AuthenticatedRoute><ManagerForm /></AuthenticatedRoute>
        },
        {
          path: '/appraisals/:id/head',
          element: <AuthenticatedRoute><HeadSignoff /></AuthenticatedRoute>
        },
        {
          path: '/appraisals/:id/head/feedback/:reviewId',
          element: <AuthenticatedRoute><ManagerForm viewerApproverLevel={2} /></AuthenticatedRoute>
        },
        {
          path: '/appraisals/:id/bu-head',
          element: <AuthenticatedRoute><BuHeadSignoff /></AuthenticatedRoute>
        },
        {
          path: '/appraisals/:id/bu-head/feedback/:reviewId',
          element: <AuthenticatedRoute><ManagerForm viewerApproverLevel={3} /></AuthenticatedRoute>
        },
        {
          path: '/appraisals/:id/approver-4',
          element: <AuthenticatedRoute><ApproverSignoffPage level={4} /></AuthenticatedRoute>
        },
        {
          path: '/appraisals/:id/approver-4/feedback/:reviewId',
          element: <AuthenticatedRoute><ManagerForm viewerApproverLevel={4} /></AuthenticatedRoute>
        },
        {
          path: '/appraisals/:id/approver-5',
          element: <AuthenticatedRoute><ApproverSignoffPage level={5} /></AuthenticatedRoute>
        },
        {
          path: '/appraisals/:id/approver-5/feedback/:reviewId',
          element: <AuthenticatedRoute><ManagerForm viewerApproverLevel={5} /></AuthenticatedRoute>
        },
        {
          path: '/account/login',
          element: <AnonymousRoute><Login /></AnonymousRoute>
        },
        {
          path: '/account/login/code',
          element: <AnonymousRoute><RequestLoginCode /></AnonymousRoute>
        },
        {
          path: '/account/login/code/confirm',
          element: <AnonymousRoute><ConfirmLoginCode /></AnonymousRoute>
        },
        {
          path: '/account/email',
          element: <AuthenticatedRoute><ChangeEmail /></AuthenticatedRoute>
        },
        {
          path: '/account/logout',
          element: <Logout />
        },
        {
          path: '/account/provider/callback',
          element: <ProviderCallback />
        },
        {
          path: '/account/provider/signup',
          element: <AnonymousRoute><ProviderSignup /></AnonymousRoute>
        },
        {
          path: '/account/providers',
          element: <AuthenticatedRoute><ManageProviders /></AuthenticatedRoute>
        },
        {
          path: '/account/signup',
          element: <AnonymousRoute><Signup /></AnonymousRoute>
        },
        {
          path: '/account/signup/passkey',
          element: <AnonymousRoute><SignupByPasskey /></AnonymousRoute>
        },
        {
          path: '/account/signup/passkey/create',
          element: <AnonymousRoute><CreateSignupPasskey /></AnonymousRoute>
        },
        {
          path: '/account/verify-email',
          element: config.data.account.email_verification_by_code_enabled ? <VerifyEmailByCode /> : <VerificationEmailSent />
        },
        {
          path: '/account/verify-email/:key',
          element: <VerifyEmail />,
          loader: verifyEmailLoader
        },
        {
          path: '/account/password/reset',
          element: <AnonymousRoute><RequestPasswordReset /></AnonymousRoute>
        },
        {
          path: '/account/password/reset/confirm',
          element: <AnonymousRoute><ConfirmPasswordResetCode /></AnonymousRoute>
        },
        {
          path: '/account/password/reset/complete',
          element: <AnonymousRoute><ResetPasswordByCode /></AnonymousRoute>
        },
        {
          path: '/account/password/reset/key/:key',
          element: <AnonymousRoute><ResetPasswordByLink /></AnonymousRoute>,
          loader: resetPasswordByLinkLoader
        },
        {
          path: '/account/password/change',
          element: <AuthenticatedRoute><ChangePassword /></AuthenticatedRoute>
        },
        {
          path: '/account/2fa',
          element: <AuthenticatedRoute><MFAOverview /></AuthenticatedRoute>,
          loader: mfaOverviewLoader
        },
        {
          path: '/account/reauthenticate',
          element: <AuthenticatedRoute><Reauthenticate /></AuthenticatedRoute>
        },
        {
          path: '/account/reauthenticate/totp',
          element: <AuthenticatedRoute><ReauthenticateTOTP /></AuthenticatedRoute>
        },
        {
          path: '/account/reauthenticate/recovery-codes',
          element: <AuthenticatedRoute><ReauthenticateRecoveryCodes /></AuthenticatedRoute>
        },
        {
          path: '/account/reauthenticate/webauthn',
          element: <AuthenticatedRoute><ReauthenticateWebAuthn /></AuthenticatedRoute>
        },
        {
          path: '/account/authenticate/totp',
          element: <AnonymousRoute><AuthenticateTOTP /></AnonymousRoute>
        },
        {
          path: '/account/2fa/trust',
          element: <AnonymousRoute><Trust /></AnonymousRoute>
        },
        {
          path: '/account/authenticate/recovery-codes',
          element: <AnonymousRoute><AuthenticateRecoveryCodes /></AnonymousRoute>
        },
        {
          path: '/account/authenticate/webauthn',
          element: <AnonymousRoute><AuthenticateWebAuthn /></AnonymousRoute>
        },
        {
          path: '/account/2fa/totp/activate',
          element: <AuthenticatedRoute><ActivateTOTP /></AuthenticatedRoute>,
          loader: activateTOTPLoader
        },
        {
          path: '/account/2fa/totp/deactivate',
          element: <AuthenticatedRoute><DeactivateTOTP /></AuthenticatedRoute>
        },
        {
          path: '/account/2fa/recovery-codes',
          element: <AuthenticatedRoute><RecoveryCodes /></AuthenticatedRoute>,
          loader: recoveryCodesLoader
        },
        {
          path: '/account/2fa/recovery-codes/generate',
          element: <AuthenticatedRoute><GenerateRecoveryCodes /></AuthenticatedRoute>,
          loader: generateRecoveryCodesLoader
        },
        {
          path: '/account/2fa/webauthn',
          element: <AuthenticatedRoute><ListWebAuthn /></AuthenticatedRoute>,
          loader: listWebAuthnLoader
        },
        {
          path: '/account/2fa/webauthn/add',
          element: <AuthenticatedRoute><AddWebAuthn /></AuthenticatedRoute>
        },
        {
          path: '/account/sessions',
          element: <AuthenticatedRoute><Sessions /></AuthenticatedRoute>
        },
        {
          path: '/sessions/:id',
          element: <AuthenticatedRoute><SessionDetail /></AuthenticatedRoute>
        },
        {
          path: '*',
          element: <NotFound />
        }
      ]
    }
  ])
}

export default function Router() {
  // If we create the router globally, the loaders of the routes already trigger
  // even before the <AuthContext/> trigger the initial loading of the auth.
  // state.
  const [router, setRouter] = useState(null)
  const config = useConfig()
  useEffect(() => {
    setRouter(createRouter(config))
  }, [config])
  return router ? <RouterProvider router={router} /> : null
}
